import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const lowLevelAlertErrorTypes = ['AuthorizeError', 'AuthenticateError', 'OAuthWindowClosed', 'OAuthWindowTimeout', 'OAuthWindowBlocked', 'MissingPermissions', 'UnknownOAuthProblem', 'DuplicateAccount'];
export function isKnownErrorType(errorType) {
  return lowLevelAlertErrorTypes.includes(errorType);
}
/** Maps a known error type to its i18n key pair, for use by any alert surface. */
export function getAuthErrorI18nKeys(errorType) {
  switch (errorType) {
    case 'AuthorizeError':
      return {
        titleKey: 'unifiedAuthCore.errors.authorize.titleText',
        messageKey: 'unifiedAuthCore.errors.authorize.default'
      };
    case 'AuthenticateError':
      return {
        titleKey: 'unifiedAuthCore.errors.authenticate.titleText',
        messageKey: 'unifiedAuthCore.errors.authenticate.default'
      };
    case 'OAuthWindowBlocked':
      return {
        titleKey: 'unifiedAuthCore.errors.oauthWindowBlocked.titleText',
        messageKey: 'unifiedAuthCore.errors.oauthWindowBlocked.instructions',
        messageUsesAppName: true
      };
    case 'OAuthWindowClosed':
      return {
        titleKey: 'unifiedAuthCore.errors.oauthWindowClosed.titleText',
        messageKey: 'unifiedAuthCore.errors.oauthWindowClosed.instructions'
      };
    case 'OAuthWindowTimeout':
      return {
        titleKey: 'unifiedAuthCore.errors.oauthWindowTimedOut.titleText',
        messageKey: 'unifiedAuthCore.errors.oauthWindowTimedOut.instructions'
      };
    case 'UnknownOAuthProblem':
      return {
        titleKey: 'unifiedAuthCore.errors.authenticate.titleText',
        messageKey: 'unifiedAuthCore.errors.pleaseTryAgain'
      };
    case 'MissingPermissions':
      return {
        titleKey: 'unifiedAuthCore.errors.missingPermissions.titleText',
        messageKey: 'unifiedAuthCore.errors.missingPermissions.body'
      };
    case 'DuplicateAccount':
      return {
        titleKey: 'unifiedAuthCore.modal.duplicateAccountHeader',
        messageKey: 'unifiedAuthCore.auth.tryDifferentAccount',
        messageUsesAppName: true
      };
    default:
      return {
        titleKey: 'unifiedAuthCore.errors.authenticate.titleText',
        messageKey: 'unifiedAuthCore.errors.authenticate.default'
      };
  }
}
function getErrorAlertConfig(appName) {
  function buildConfig(errorType) {
    const keys = getAuthErrorI18nKeys(errorType);
    return {
      alertType: 'danger',
      title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: keys.titleKey,
        options: {
          appName
        }
      }),
      description: keys.messageUsesAppName ? () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: keys.messageKey,
        options: {
          appName
        }
      }) : () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: keys.messageKey
      })
    };
  }
  return {
    AuthorizeError: buildConfig('AuthorizeError'),
    AuthenticateError: buildConfig('AuthenticateError'),
    OAuthWindowBlocked: buildConfig('OAuthWindowBlocked'),
    OAuthWindowClosed: buildConfig('OAuthWindowClosed'),
    OAuthWindowTimeout: buildConfig('OAuthWindowTimeout'),
    UnknownOAuthProblem: buildConfig('UnknownOAuthProblem'),
    MissingPermissions: buildConfig('MissingPermissions'),
    DuplicateAccount: buildConfig('DuplicateAccount')
  };
}
export function LowLevelAuthErrorAlert({
  appName,
  error,
  context,
  processError
}) {
  var _config$description;
  const defaultConfig = useMemo(() => getErrorAlertConfig(appName), [appName]);
  const processedError = processError === null || processError === void 0 ? void 0 : processError({
    appName,
    error,
    defaultConfig,
    context
  });
  if ((processedError === null || processedError === void 0 ? void 0 : processedError.presentation) === 'component') {
    return processedError.render({
      error,
      context
    });
  }
  const effectiveError = processedError !== null && processedError !== void 0 && processedError.errorOverride && processedError.presentation === 'alert' ? processedError.errorOverride : error;
  const config = (() => {
    if ((processedError === null || processedError === void 0 ? void 0 : processedError.presentation) === 'alert') {
      return processedError.config;
    }
    if (isKnownErrorType(effectiveError.type)) {
      return defaultConfig[effectiveError.type];
    }
    console.warn(`[LowLevelAuthErrorAlert] Unknown error type "${effectiveError.type}" encountered. ` + 'Consider providing a processError handler for custom error types.');
    return undefined;
  })();
  if (!config) {
    return null;
  }
  const authenticateErrorDetails = effectiveError.type === 'AuthenticateError' ? effectiveError : undefined;
  const errorMessage = effectiveError.type === 'AuthorizeError' || effectiveError.type === 'AuthenticateError' ? effectiveError.message : undefined;
  return /*#__PURE__*/_jsxs(Alert, {
    type: config.alertType,
    titleText: config.title(appName),
    children: [errorMessage || ((_config$description = config.description) === null || _config$description === void 0 ? void 0 : _config$description.call(config, appName)), (authenticateErrorDetails === null || authenticateErrorDetails === void 0 ? void 0 : authenticateErrorDetails.upstreamErrorMessage) && /*#__PURE__*/_jsx(AccordionItem, {
      marginBlockStart: "400",
      title: /*#__PURE__*/_jsx("strong", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "unifiedAuthCore.errors.accountCreation.details"
        })
      }),
      children: /*#__PURE__*/_jsx("p", {
        style: {
          whiteSpace: 'pre-line'
        },
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "unifiedAuthCore.errors.accountCreation.htmlMessage",
          options: {
            message: authenticateErrorDetails.upstreamErrorMessage
          }
        })
      })
    })]
  });
}